import React from 'react'
import { XMarkIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline'
import { SelectDropdown } from '../../components/SelectDropdown'
import { METER_FIELD_HINTS } from '../../utils/igtMeterTemplate'
import { PATRON_FIELD_HINTS, resolveSourceLabelToColumn } from '../../utils/igtPatronTemplate'
import {
  importMappingFieldsForType,
  importMappingFieldHint,
} from '../../utils/importMappingFieldCatalog'

export const IMPORT_MAPPING_TYPE_OPTIONS = [
  { value: 'meter', label: 'Meter → egm_meter_readings' },
  { value: 'sessions', label: 'Sessions → gaming_sessions' },
  { value: 'egm', label: 'EGM Registry → egm_registry' },
  { value: 'patron', label: 'Patron (DimPatron) → patron_profiles' },
  { value: 'slot_analysis', label: 'Slot analysis → fact_slot_analysis_daily' },
  { value: 'visitor', label: 'Visitor → visitor_entries' },
]

export const VENDOR_OPTIONS = [
  { value: 'Aristocrat', label: 'Aristocrat' },
  { value: 'IGT', label: 'IGT' },
  { value: 'Ebet', label: 'Ebet' },
  { value: 'Ainsworth', label: 'Ainsworth' },
  { value: 'Konami', label: 'Konami' },
  { value: 'Scientific Games', label: 'Scientific Games' },
  { value: 'RXConnect', label: 'RXConnect / gateway' },
  { value: 'Other', label: 'Other' },
]

export const TEMPLATE_TYPE_OPTIONS = [
  { value: 'metering', label: 'Metering' },
  { value: 'gaming', label: 'Gaming' },
  { value: 'patron', label: 'Patron' },
  { value: 'slot_analysis', label: 'Slot analysis' },
  { value: 'visitor', label: 'Visitor' },
]

export const SOURCE_FORMAT_OPTIONS = [
  { value: 'pipe-delimited', label: 'Pipe (|)' },
  { value: 'csv', label: 'Comma (,)' },
]

export const DEFAULT_FILE_PREFIX_BY_TYPE = {
  meter: 'Meter_',
  sessions: 'FactPlayerSession',
  egm: 'DimMachine',
  patron: 'DimPatron',
  slot_analysis: 'FactSlotAnalysisDaily',
  visitor: 'DimVisitor',
}

export function defaultFilePrefixForType(mappingType) {
  return DEFAULT_FILE_PREFIX_BY_TYPE[mappingType] || ''
}

function normalizeSourceFormatValue(fmt) {
  const s = String(fmt || '').trim().toLowerCase()
  if (s === 'csv' || s === 'comma') return 'csv'
  return 'pipe-delimited'
}

export function sourceFormatLabel(fmt) {
  return normalizeSourceFormatValue(fmt) === 'csv' ? 'Comma (,)' : 'Pipe (|)'
}

export { importMappingFieldsForType } from '../../utils/importMappingFieldCatalog'

function fieldHint(mappingType, field) {
  return importMappingFieldHint(mappingType, field)
    || (mappingType === 'meter' ? METER_FIELD_HINTS[field] : null)
    || (mappingType === 'patron' ? PATRON_FIELD_HINTS[field] : null)
}

export function emptyPipeMapping() {
  return {
    sourceFormat: 'pipe-delimited',
    filePrefix: '',
    mappings: {},
    sourceLabels: {},
  }
}

export function ensurePipeMapping(cfg) {
  if (!cfg || typeof cfg !== 'object') return emptyPipeMapping()
  return {
    sourceFormat: normalizeSourceFormatValue(cfg.sourceFormat),
    filePrefix: cfg.filePrefix != null ? String(cfg.filePrefix) : '',
    venueCode: cfg.venueCode,
    skipRows: cfg.skipRows,
    sourceLabels: cfg.sourceLabels ? { ...cfg.sourceLabels } : {},
    mappings: { ...(cfg.mappings || {}) },
  }
}

function displayPipeColumn(raw, sourceLabels) {
  if (raw == null || String(raw).trim() === '') return ''
  const s = String(raw)
  if (s.startsWith('derive:')) return s
  const pipeCol = resolveSourceLabelToColumn(s, sourceLabels)
  return pipeCol != null ? String(pipeCol) : s
}

/**
 * Read-only column map for the current template panel.
 * @param {{ mappingType: string, cfg: object|null, maxHeightClass?: string }} props
 */
export function ImportMappingReadonlyTable({ mappingType, cfg, maxHeightClass = 'max-h-72' }) {
  const pipeCfg = ensurePipeMapping(cfg)
  const mappings = pipeCfg.mappings || {}
  const sourceLabels = pipeCfg.sourceLabels || {}
  const fields = importMappingFieldsForType(mappingType)
  const mapped = fields
    .map((f) => ({ field: f, col: displayPipeColumn(mappings[f], sourceLabels) }))
    .filter((row) => row.col !== '')

  if (!mapped.length) {
    return (
      <p className="text-sm text-gray-500 py-2">No column mappings loaded for this template.</p>
    )
  }

  const delimiterLabel = sourceFormatLabel(pipeCfg.sourceFormat)
  const prefixLabel = String(pipeCfg.filePrefix || '').trim() || defaultFilePrefixForType(mappingType) || '—'

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        Delimiter: {delimiterLabel}
        {' · '}
        Filename prefix: {prefixLabel}
        {!String(pipeCfg.filePrefix || '').trim() ? ' (default)' : ''}
      </p>
      <div className={`overflow-auto border border-gray-200 rounded-lg bg-white ${maxHeightClass}`}>
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-700">PaySafeGo field</th>
            <th className="px-3 py-2 text-left font-medium text-gray-700 w-28">Column #</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {mapped.map(({ field, col }) => (
            <tr key={field}>
              <td className="px-3 py-1.5 font-mono text-gray-800">
                <div>{field}</div>
                {fieldHint(mappingType, field) && (
                  <div className="text-xs font-sans mt-0.5 text-gray-500">{fieldHint(mappingType, field)}</div>
                )}
              </td>
              <td className="px-3 py-1.5 font-mono text-gray-700">{col}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  )
}

/**
 * Modal to create or edit a delimited import mapping template.
 */
export default function ImportMappingTemplateModal({
  open,
  mode = 'new',
  mappingType,
  draft,
  onDraftChange,
  templateName,
  onTemplateNameChange,
  templateType,
  onTemplateTypeChange,
  vendor,
  onVendorChange,
  saving = false,
  onClose,
  onSave,
  onImportJson,
}) {
  if (!open) return null

  const cfg = ensurePipeMapping(draft)
  const fields = importMappingFieldsForType(mappingType)
  const mappings = cfg.mappings || {}
  const sourceLabels = cfg.sourceLabels || {}
  const title = mode === 'edit' ? 'Edit template' : 'New template'
  const defaultPrefix = defaultFilePrefixForType(mappingType)

  const setFieldMapping = (field, value) => {
    const nextMaps = { ...(mappings || {}) }
    if (value === '' || value == null) delete nextMaps[field]
    else nextMaps[field] = value
    onDraftChange({ ...cfg, mappings: nextMaps })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Column numbers (0-based). Delimiter: {sourceFormatLabel(cfg.sourceFormat)}. Import type: {mappingType}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-md border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
            aria-label="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 min-h-0 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Template name</label>
              <input
                className="input-field w-full"
                value={templateName}
                onChange={(e) => onTemplateNameChange(e.target.value)}
                placeholder="e.g. S7000_EGMMeters"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Type</label>
              <SelectDropdown
                className="w-full"
                value={templateType}
                onChange={(e) => onTemplateTypeChange(e.target.value)}
                options={TEMPLATE_TYPE_OPTIONS}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Vendor</label>
              <SelectDropdown
                className="w-full"
                value={vendor}
                onChange={(e) => onVendorChange(e.target.value)}
                options={VENDOR_OPTIONS}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Delimiter</label>
              <SelectDropdown
                className="w-full"
                value={cfg.sourceFormat}
                onChange={(e) => onDraftChange({ ...cfg, sourceFormat: e.target.value })}
                options={SOURCE_FORMAT_OPTIONS}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Filename prefix</label>
              <input
                className="input-field w-full font-mono text-sm"
                value={cfg.filePrefix || ''}
                onChange={(e) => onDraftChange({ ...cfg, filePrefix: e.target.value })}
                placeholder={defaultPrefix || 'e.g. DimPatron'}
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2">
            Daily Data picks files whose name starts with this prefix (e.g. {defaultPrefix || 'DimPatron'}…-Batch-1.txt).
            Leave blank to use the system default for this import type.
          </p>

          {typeof onImportJson === 'function' && (
            <div>
              <button
                type="button"
                onClick={onImportJson}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-50"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                Import mapping JSON
              </button>
              <p className="text-xs text-gray-500 mt-1">Loads column map into this form (then save as template).</p>
            </div>
          )}

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">PaySafeGo field</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700 w-40">Column #</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields.map((f) => {
                  const raw = mappings[f] ?? ''
                  const pipeDisplay = displayPipeColumn(raw, sourceLabels)
                  return (
                    <tr key={f} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-gray-800">
                        <div>{f}</div>
                        {fieldHint(mappingType, f) && (
                          <div className="text-xs font-sans mt-0.5 text-gray-500">
                            {fieldHint(mappingType, f)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        {String(raw).startsWith('derive:') ? (
                          <input
                            className="input-field w-full max-w-xs font-mono"
                            value={raw}
                            onChange={(e) => setFieldMapping(f, e.target.value)}
                          />
                        ) : (
                          <input
                            type="number"
                            min={0}
                            className="input-field w-24 font-mono"
                            value={pipeDisplay}
                            placeholder="—"
                            onChange={(e) => {
                              const v = e.target.value.trim()
                              if (v === '') {
                                setFieldMapping(f, '')
                                return
                              }
                              const n = parseInt(v, 10)
                              if (Number.isNaN(n) || n < 0) return
                              setFieldMapping(f, String(n))
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200 shrink-0">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            disabled={saving || !String(templateName || '').trim()}
            onClick={onSave}
          >
            {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Save as template & set current'}
          </button>
        </div>
      </div>
    </div>
  )
}
